/**
 * Thin, pure query helpers over compendium records — the small, drift-prone glue shared by
 * both editor worlds (the kit leaves in `lib/editor/` and the hand-CSS brutalist editors in
 * `routes/home/`). Deliberately NOT a composite `affordEquip`: the per-picker SP/size/type
 * filters stay inline at their call sites, where they read clearer than behind a
 * `type`-branching abstraction. Only helpers that are genuinely identical everywhere — and
 * a correctness risk if one copy drifted — belong here.
 */

/**
 * The license-id slug: lower-case, collapse non-alphanumeric runs to single hyphens,
 * trim leading/trailing hyphens. This is the SAME derivation the reducer's equip gate /
 * `licenseManufacturers` use (frame name → license id), so leaf-derived ids line up with
 * the pilot's `licenses` keys and the gate can't drift from the control.
 */
export function licenseSlug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Sort compendium records (or any `{ name? }` view-model) by `name`, ascending and
 * locale-aware. Returns a NEW array — never mutates the input — and treats a missing
 * name as the empty string, matching every call site's `(a.name ?? "")` comparator.
 */
export function sortByName<T extends { name?: string }>(items: readonly T[]): T[] {
  return items.slice().sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
}

/**
 * Keep only the items the pilot doesn't yet own — those whose count in the `owned` map
 * (e.g. `pilot.skills` / `pilot.talents`, a `Record<id, rank>`) is absent or zero.
 * Returns a new array; does not mutate the input.
 */
export function filterUnowned<T extends { id: string }>(
  items: readonly T[],
  owned: Record<string, number>,
): T[] {
  return items.filter((item) => (owned[item.id] ?? 0) === 0);
}
